import { ToolBase, ToolPointer } from './tool';
import { Vec3, dist } from '../core/vec';
import { formatLength, parseLength } from '../core/units';
import { SNAP_COLORS } from '../render/preview';
import { SnapResult } from '../inference/inference';

/**
 * Maßband (T): zwei Punkte messen und Hilfslinie anlegen.
 * Klick auf eine Kante + Ziehen erzeugt eine parallele Hilfslinie im festen Abstand.
 */
export class TapeTool extends ToolBase {
  readonly id = 'tape';
  readonly label = 'Maßband';
  readonly shortcut = 'T';
  readonly cursor = 'crosshair';
  readonly hint = 'Punkt 1, Punkt 2 = messen + Hilfslinie · Von einer Kante ziehen = parallele Hilfslinie';

  private p1: Vec3 | null = null;
  private p1EdgeId: number | null = null;
  private current: Vec3 | null = null;
  private snap: SnapResult | null = null;

  enter(): void {
    this.cancel();
  }

  exit(): void {
    this.ctx.preview.clear();
    this.p1 = null;
    this.p1EdgeId = null;
  }

  pointerMove(e: ToolPointer): void {
    this.ctx.preview.clear();
    const snap = this.ctx.inference.snap(e.x, e.y, e.ray, { fallbackPlane: null });
    this.snap = snap;
    if (snap.kind !== 'ground') this.ctx.preview.dot(snap.point, SNAP_COLORS[snap.kind] ?? 0x888888, 8);
    this.current = snap.point;

    if (this.p1) {
      if (this.p1EdgeId !== null) {
        // Parallele Hilfslinie zur Kante im Abstand |p1→cursor|
        const edge = this.ctx.model.edges.get(this.p1EdgeId);
        if (edge) {
          const [a, b] = this.ctx.model.edgePoints(edge);
          this.ctx.preview.polyline(this.offsetSegment(a, b, snap.point), 0x9a68d8);
          this.ctx.ui.setStatus(`Abstand: ${formatLength(dist(this.p1, snap.point), this.ctx.units())} · Klicken = Hilfslinie · Zahl tippen = Abstand`);
        }
      } else {
        this.ctx.preview.line(this.p1, snap.point, 0x9a68d8);
        this.ctx.ui.setStatus(`Länge: ${formatLength(dist(this.p1, snap.point), this.ctx.units())} · Klicken = messen + Hilfslinie`);
      }
    }
    this.ctx.requestUpdate();
  }

  /** Zur Kante parallele Strecke durch den Cursor (gleiche Länge, verschoben). */
  private offsetSegment(a: Vec3, b: Vec3, cursor: Vec3): Vec3[] {
    const abx = b.x - a.x, aby = b.y - a.y, abz = b.z - a.z;
    const dx = cursor.x - this.p1!.x, dy = cursor.y - this.p1!.y, dz = cursor.z - this.p1!.z;
    return [
      { x: a.x + dx, y: a.y + dy, z: a.z + dz },
      { x: b.x + dx, y: b.y + dy, z: b.z + dz }
    ];
  }

  pointerDown(e: ToolPointer): void {
    if (e.button !== 0) return;
    this.pointerMove(e); // Frischen Snap für die aktuelle Position holen
    if (!this.p1) {
      this.p1 = this.snap!.point;
      // Parallele Hilfslinie nur beim Start auf einer Kante (nicht an deren Mittel-/Endpunkt, wie SketchUp)
      this.p1EdgeId = this.snap!.kind === 'onEdge' ? this.snap!.edgeId ?? null : null;
      this.ctx.ui.setStatus(
        this.p1EdgeId !== null ? 'Abstand ziehen oder Zahl tippen' : 'Zielpunkt wählen'
      );
    } else {
      this.commit();
    }
    this.ctx.requestUpdate();
  }

  private commit(distanceOverride?: number): void {
    if (!this.p1 || !this.current) return;
    this.ctx.beginEdit();
    if (this.p1EdgeId !== null) {
      const edge = this.ctx.model.edges.get(this.p1EdgeId);
      if (edge) {
        const [a, b] = this.ctx.model.edgePoints(edge);
        let target = this.current;
        if (distanceOverride !== undefined) {
          // Abstand senkrecht zur Kante messen (Seite aus der Cursorlage), nicht entlang der Cursorrichtung
          const ab = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z };
          const abLen = Math.hypot(ab.x, ab.y, ab.z) || 1;
          const u = { x: ab.x / abLen, y: ab.y / abLen, z: ab.z / abLen };
          const d = { x: this.current.x - this.p1.x, y: this.current.y - this.p1.y, z: this.current.z - this.p1.z };
          const along = d.x * u.x + d.y * u.y + d.z * u.z;
          const perp = { x: d.x - u.x * along, y: d.y - u.y * along, z: d.z - u.z * along };
          const len = Math.hypot(perp.x, perp.y, perp.z);
          if (len < 1e-9) return;
          target = {
            x: this.p1.x + (perp.x / len) * distanceOverride,
            y: this.p1.y + (perp.y / len) * distanceOverride,
            z: this.p1.z + (perp.z / len) * distanceOverride
          };
        }
        const seg = this.offsetSegment(a, b, target);
        this.ctx.model.addGuide(seg[0]!, seg[1]!, true);
      }
    } else {
      let end = this.current;
      if (distanceOverride !== undefined) {
        // Punkt-zu-Punkt: getippte Länge in Cursorrichtung
        const d = { x: end.x - this.p1.x, y: end.y - this.p1.y, z: end.z - this.p1.z };
        const len = Math.hypot(d.x, d.y, d.z);
        if (len < 1e-9) return;
        end = { x: this.p1.x + (d.x / len) * distanceOverride, y: this.p1.y + (d.y / len) * distanceOverride, z: this.p1.z + (d.z / len) * distanceOverride };
      }
      this.ctx.model.addGuide(this.p1, end, false);
    }
    this.p1 = null;
    this.p1EdgeId = null;
    this.ctx.preview.clear();
    this.ctx.requestUpdate();
  }

  wantsVcb(): boolean {
    return this.p1 !== null;
  }

  vcbDisplay(): string | null {
    if (!this.p1 || !this.current) return null;
    return formatLength(dist(this.p1, this.current), this.ctx.units());
  }

  applyVcb(raw: string): void {
    const value = parseLength(raw, this.ctx.units());
    if (value === null || value <= 0) return;
    this.commit(value);
  }

  cancel(): void {
    this.p1 = null;
    this.p1EdgeId = null;
    this.ctx.preview.clear();
    this.ctx.ui.setStatus(null);
    this.ctx.requestUpdate();
  }
}
